import supabase from '../lib/supabaseClient';

/**
 * Quote Service - Handles all quote-related database operations
 * Implements chunked saving as per database schema
 */

// ============================================================================
// STEP 1: Location Information
// ============================================================================

/**
 * Create initial quote with location data
 * @param {Object} locationData - Origin and destination details
 * @returns {Object} Created quote with ID
 */
export const createQuote = async (locationData) => {
  try {
    const { data, error } = await supabase
      .from('get_quote')
      .insert([
        {
          origin_postal_code: locationData.origin_zip,
          origin_city: locationData.origin_city,
          origin_state: locationData.origin_state,
          origin_address: locationData.origin_address,
          origin_latitude: locationData.origin_latitude,
          origin_longitude: locationData.origin_longitude,
          destination_postal_code: locationData.destination_zip,
          destination_city: locationData.destination_city,
          destination_state: locationData.destination_state,
          destination_address: locationData.destination_address,
          destination_latitude: locationData.destination_latitude,
          destination_longitude: locationData.destination_longitude,
          move_date: locationData.move_date,
          move_time: locationData.move_time,
          status: 'draft'
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Update quote location data
 */
export const updateQuoteLocation = async (quoteId, locationData) => {
  try {
    const { data, error } = await supabase
      .from('get_quote')
      .update({
        origin_postal_code: locationData.fromZip,
        origin_city: locationData.fromCity,
        origin_state: locationData.fromState,
        origin_address: locationData.fromAddress,
        destination_postal_code: locationData.toZip,
        destination_city: locationData.toCity,
        destination_state: locationData.toState,
        destination_address: locationData.toAddress,
        move_date: locationData.fromDate,
        move_time: locationData.fromTime,
      })
      .eq('id', quoteId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// STEP 2: Move Type & Assistance
// ============================================================================

/**
 * Save move type and assistance details
 */
export const saveMoveDetails = async (quoteId, moveDetails) => {
  try {
    const { data, error } = await supabase
      .from('quote_move_details')
      .upsert([
        {
          get_quote_id: quoteId,
          move_type: moveDetails.move_type,
          assistance_type: moveDetails.assistance_type
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// STEP 3: Property/Conditional Details
// ============================================================================

/**
 * Save property size (for standard moves)
 */
export const savePropertySize = async (quoteId, propertyData) => {
  try {
    const { data, error } = await supabase
      .from('property_size')
      .upsert([
        {
          get_quote_id: quoteId,
          property_type: propertyData.property_size,
          size_sqft: propertyData.size_sqft || null,
          number_of_rooms: propertyData.number_of_rooms || null
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Save piano move details
 */
export const savePianoMove = async (quoteId, pianoData) => {
  try {
    const { data, error } = await supabase
      .from('piano_move')
      .upsert([
        {
          get_quote_id: quoteId,
          piano_type: pianoData.piano_type,
          number_of_stairs: parseInt(pianoData.floors_stairs) || 0,
          floor_level: pianoData.floor_level || null,
          has_elevator: pianoData.has_elevator || false
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Save truck/container rental details
 */
export const saveTruckContainerMove = async (quoteId, truckData) => {
  try {
    const { data, error } = await supabase
      .from('truck_container_move')
      .upsert([
        {
          get_quote_id: quoteId,
          size: truckData.truck_size,
          rental_duration_days: truckData.rental_duration_days || null
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Save vehicle move details
 */
export const saveVehicleMove = async (quoteId, vehicleData) => {
  try {
    const { data, error } = await supabase
      .from('vehicle_move')
      .upsert([
        {
          get_quote_id: quoteId,
          vehicle_type: vehicleData.vehicle_type,
          make: vehicleData.vehicle_make,
          model: vehicleData.vehicle_model,
          year: parseInt(vehicleData.vehicle_year) || null,
          is_running: vehicleData.is_running !== false
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// STEP 4: Project Details
// ============================================================================

/**
 * Save project description and notes
 */
export const saveProjectDetails = async (quoteId, projectData) => {
  try {
    const { data, error } = await supabase
      .from('quote_project_details')
      .upsert([
        {
          get_quote_id: quoteId,
          project_description: projectData.project_description,
          special_items: projectData.special_items || null,
          additional_notes: projectData.additional_notes || null
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// STEP 5: User Contact & Account Creation
// ============================================================================

/**
 * Send OTP to user's phone number
 */
export const sendOTP = async (phoneNumber) => {
  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone: phoneNumber,
      options: {
        channel: 'sms'
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Verify OTP code
 */
export const verifyOTP = async (phoneNumber, token) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      phone: phoneNumber,
      token: token,
      type: 'sms'
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Create or update user and link to quote
 */
export const saveUserAndLinkQuote = async (quoteId, userData) => {
  try {
    // Check if user exists by email
    const { data: existingUser } = await supabase
      .from('normal_user')
      .select('id')
      .eq('email', userData.email)
      .single();

    let userId;

    if (existingUser) {
      // Update existing user
      const { data: updatedUser, error: updateError } = await supabase
        .from('normal_user')
        .update({
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone: userData.phone_number
        })
        .eq('id', existingUser.id)
        .select()
        .single();

      if (updateError) throw updateError;
      userId = updatedUser.id;
    } else {
      // Create new user
      const { data: newUser, error: insertError } = await supabase
        .from('normal_user')
        .insert([
          {
            first_name: userData.first_name,
            last_name: userData.last_name,
            phone: userData.phone_number,
            email: userData.email
          }
        ])
        .select()
        .single();

      if (insertError) throw insertError;
      userId = newUser.id;
    }

    // Link user to quote
    const { data: updatedQuote, error: linkError } = await supabase
      .from('get_quote')
      .update({ user_id: userId })
      .eq('id', quoteId)
      .select()
      .single();

    if (linkError) throw linkError;

    return { success: true, data: { userId, quote: updatedQuote } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// USER LIVE LOCATION
// ============================================================================

/**
 * Save user's live location (for proximity matching)
 */
export const saveUserLiveLocation = async (locationData) => {
  try {
    const { data, error } = await supabase
      .from('user_live_location')
      .insert([
        {
          user_id: locationData.userId || null,
          quote_id: locationData.quoteId || null,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy || null,
          formatted_address: locationData.formattedAddress || null,
          city: locationData.city || null,
          state: locationData.state || null,
          country: locationData.country || null,
          postal_code: locationData.postalCode || null,
          ip_address: locationData.ipAddress || null,
          user_agent: locationData.userAgent || null
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get user's live location
 */
export const getUserLiveLocation = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_live_location')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// SELECTED MOVERS
// ============================================================================

/**
 * Get movers in a specific state, sorted by distance from destination ZIP
 */
export const getMoversByState = async (state, destinationZip = null) => {
  try {
    const { data, error } = await supabase
      .from('movers_data')
      .select('*')
      .eq('state', state)
      .eq('is_active', true)
      .order('company_name');

    if (error) throw error;


    // Filter movers based on lead capacity
    // Only show movers who have been allocated leads (total_leads > 0) AND have remaining capacity
    const availableMovers = (data || []).filter(mover => {
      const totalLeads = mover.total_leads || 0;
      const consumedLeads = mover.consumed_leads || 0;
      const isPaused = mover.account_paused === true;

      // Filter out paused accounts
      if (isPaused) {
        return false;
      }

      // If total_leads is 0, mover has NO allocation - don't show
      if (totalLeads === 0) {
        return false;
      }

      // If consumed_leads is less than total_leads, mover has capacity
      const hasCapacity = consumedLeads < totalLeads;
      return hasCapacity;
    });


    // Fetch ratings and review counts for available movers
    const moversWithRatings = await Promise.all(
      availableMovers.map(async (mover) => {
        // Get approved reviews for this mover
        const { data: reviews, error: reviewError } = await supabase
          .from('mover_reviews')
          .select('rating')
          .eq('mover_id', mover.id)
          .eq('is_approved', true);

        let rating = 0;
        let total_reviews = 0;

        if (!reviewError && reviews && reviews.length > 0) {
          total_reviews = reviews.length;
          const sumRatings = reviews.reduce((sum, review) => sum + review.rating, 0);
          rating = parseFloat((sumRatings / total_reviews).toFixed(1));
        }

        return {
          ...mover,
          rating,
          total_reviews
        };
      })
    );

    // If destination ZIP is provided, calculate distances
    if (destinationZip && moversWithRatings) {
      const moversWithDistance = await Promise.all(
        moversWithRatings.map(async (mover) => {
          const distance = await calculateZipDistance(destinationZip, mover.zip_code);
          return {
            ...mover,
            distance: distance || null
          };
        })
      );

      // Sort by distance (closest first), movers without distance go to the end
      moversWithDistance.sort((a, b) => {
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });

      return { success: true, movers: moversWithDistance };
    }

    return { success: true, movers: moversWithRatings || [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Calculate distance between two ZIP codes using their coordinates
 */
const calculateZipDistance = async (zip1, zip2) => {
  try {
    // Fetch coordinates for both ZIP codes
    const [coords1, coords2] = await Promise.all([
      getZipCoordinates(zip1),
      getZipCoordinates(zip2)
    ]);

    if (!coords1 || !coords2) return null;

    // Calculate distance using Haversine formula
    const R = 3959; // Earth's radius in miles
    const dLat = toRad(coords2.lat - coords1.lat);
    const dLon = toRad(coords2.lng - coords1.lng);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(coords1.lat)) * Math.cos(toRad(coords2.lat)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  } catch (error) {
    return null;
  }
};

/**
 * Get coordinates for a ZIP code using zippopotam.us API
 */
const getZipCoordinates = async (zipCode) => {
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
    if (!response.ok) return null;

    const data = await response.json();
    if (data.places && data.places.length > 0) {
      return {
        lat: parseFloat(data.places[0].latitude),
        lng: parseFloat(data.places[0].longitude)
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Convert degrees to radians
 */
const toRad = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Save selected movers for a quote
 */
export const saveSelectedMovers = async (quoteId, moverIds) => {
  try {
    const insertData = moverIds.map(moverId => ({
      quote_id: quoteId,
      mover_id: moverId
    }));

    const { data, error } = await supabase
      .from('quote_selected_movers')
      .insert(insertData)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Create a lead referencing the quote
 */
export const createLeadsFromQuote = async (quoteId, moverIds) => {
  try {

    // Fetch the main quote data
    const { data: quote, error: quoteError } = await supabase
      .from('get_quote')
      .select('*')
      .eq('id', quoteId)
      .single();

    if (quoteError) throw quoteError;

    // Fetch user details
    const { data: user } = await supabase
      .from('normal_user')
      .select('*')
      .eq('id', quote.user_id)
      .maybeSingle();

    // Fetch move details from quote_move_details table
    const { data: moveDetails } = await supabase
      .from('quote_move_details')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();


    // Fetch property size from property_size table
    const { data: propertyData } = await supabase
      .from('property_size')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();


    // Fetch project details from quote_project_details table
    const { data: projectDetails } = await supabase
      .from('quote_project_details')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();


    // Fetch piano details if applicable
    const { data: pianoDetails } = await supabase
      .from('piano_move')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();

    // Fetch truck details if applicable
    const { data: truckDetails } = await supabase
      .from('truck_container_move')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();

    // Fetch vehicle details if applicable
    const { data: vehicleDetails } = await supabase
      .from('vehicle_move')
      .select('*')
      .eq('get_quote_id', quoteId)
      .maybeSingle();


    // Create a single lead record with all quote data
    const leadData = {
      get_quote_id: quoteId,
      status: 'Pending',
      selected_mover_ids: moverIds, // Store user-selected mover IDs as array

      // User information
      user_id: quote.user_id,
      customer_first_name: user?.first_name || null,
      customer_last_name: user?.last_name || null,
      customer_phone: user?.phone || null,
      customer_email: user?.email || null,

      // Location data from get_quote
      origin_postal_code: quote.origin_postal_code,
      origin_city: quote.origin_city,
      origin_state: quote.origin_state,
      origin_address: quote.origin_address,
      origin_latitude: quote.origin_latitude,
      origin_longitude: quote.origin_longitude,
      destination_postal_code: quote.destination_postal_code,
      destination_city: quote.destination_city,
      destination_state: quote.destination_state,
      destination_address: quote.destination_address,
      destination_latitude: quote.destination_latitude,
      destination_longitude: quote.destination_longitude,

      // Move details from get_quote
      move_date: quote.move_date,
      move_time: quote.move_time,

      // Move details from quote_move_details table
      move_type: moveDetails?.move_type || null,
      assistance_type: moveDetails?.assistance_type || null,

      // Property size from property_size table
      property_size: propertyData?.property_size || null,

      // Piano details (if applicable)
      piano_type: pianoDetails?.piano_type || null,
      piano_floors_stairs: pianoDetails?.piano_floors_stairs || null,

      // Truck details (if applicable)
      truck_size: truckDetails?.truck_size || null,

      // Vehicle details (if applicable)
      vehicle_type: vehicleDetails?.vehicle_type || null,
      vehicle_make: vehicleDetails?.make || null,
      vehicle_model: vehicleDetails?.model || null,
      vehicle_year: vehicleDetails?.year || null,

      // Project description from quote_project_details table
      project_description: projectDetails?.project_description || null
    };


    const { data, error } = await supabase
      .from('leads')
      .insert([leadData])
      .select();

    if (error) {
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// FINALIZE QUOTE
// ============================================================================

/**
 * Mark quote as submitted (final step)
 */
export const submitQuote = async (quoteId) => {
  try {
    const { data, error } = await supabase
      .from('get_quote')
      .update({
        status: 'submitted',
        converted_to_leads: true
      })
      .eq('id', quoteId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Delete all quote-related data after converting to leads
 */
export const deleteQuoteData = async (quoteId) => {
  try {

    // STEP 0: Verify quote exists
    const { error: checkError } = await supabase
      .from('get_quote')
      .select('id')
      .eq('id', quoteId)
      .single();

    if (checkError) {
      return { success: false, error: 'Quote not found' };
    }


    // STEP 1: Remove the get_quote_id reference from leads to avoid foreign key constraint
    const { error: updateError } = await supabase
      .from('leads')
      .update({ get_quote_id: null })
      .eq('get_quote_id', quoteId);

    if (updateError) {
      throw updateError;
    }

    // STEP 2: Delete from all related tables
    const deleteResults = await Promise.all([
      supabase.from('quote_selected_movers').delete().eq('quote_id', quoteId),
      supabase.from('quote_move_details').delete().eq('get_quote_id', quoteId),
      supabase.from('quote_project_details').delete().eq('get_quote_id', quoteId),
      supabase.from('property_size').delete().eq('get_quote_id', quoteId),
      supabase.from('piano_move').delete().eq('get_quote_id', quoteId),
      supabase.from('truck_container_move').delete().eq('get_quote_id', quoteId),
      supabase.from('vehicle_move').delete().eq('get_quote_id', quoteId)
    ]);

    // STEP 3: Finally delete the main quote record from get_quote table
    const { error } = await supabase
      .from('get_quote')
      .delete()
      .eq('id', quoteId);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// RETRIEVE QUOTES
// ============================================================================

/**
 * Get complete quote with all related data
 */
export const getCompleteQuote = async (quoteId) => {
  try {
    // Get main quote
    const { data: quote, error: quoteError } = await supabase
      .from('get_quote')
      .select('*')
      .eq('id', quoteId)
      .single();

    if (quoteError) throw quoteError;

    // Get related data
    const [moveDetails, projectDetails, propertySize, pianoMove, truckMove, vehicleMove, selectedMovers] = await Promise.all([
      supabase.from('quote_move_details').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('quote_project_details').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('property_size').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('piano_move').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('truck_container_move').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('vehicle_move').select('*').eq('get_quote_id', quoteId).maybeSingle(),
      supabase.from('quote_selected_movers').select('mover_id, movers_data(*)').eq('quote_id', quoteId)
    ]);

    return {
      success: true,
      quote: {
        ...quote,
        moveDetails: moveDetails.data,
        projectDetails: projectDetails.data,
        propertySize: propertySize.data,
        pianoMove: pianoMove.data,
        truckMove: truckMove.data,
        vehicleMove: vehicleMove.data,
        selectedMovers: selectedMovers.data
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all quotes for a user
 */
export const getUserQuotes = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('get_quote')
      .select(`
        *,
        quote_move_details(*),
        quote_project_details(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all leads assigned to a specific mover, optionally filtered by team member
 * @param {string} moverId - The mover's ID from movers_data table
 * @param {string|null} teamMemberId - Optional team member ID to filter leads
 * @returns {Object} - { success, leads }
 */
export const getMoverLeads = async (moverId, teamMemberId = null) => {
  try {
    // Build the query - fetch leads where this mover is in the assigned_mover_ids array
    let query = supabase
      .from('leads')
      .select(`
        *,
        id,
        status,
        customer_first_name,
        customer_last_name,
        customer_email,
        customer_phone,
        origin_address,
        origin_city,
        origin_state,
        origin_postal_code,
        destination_address,
        destination_city,
        destination_state,
        destination_postal_code,
        move_date,
        move_type,
        assistance_type,
        property_size,
        vehicle_type,
        vehicle_make,
        vehicle_model,
        vehicle_year,
        piano_type,
        piano_floors_stairs,
        truck_size,
        project_description,
        created_at,
        assigned_team_member_id
      `)
      .contains('assigned_mover_ids', [moverId]);

    // If team member ID is provided, filter to only show leads assigned to that team member
    if (teamMemberId) {
      query = query.eq('assigned_team_member_id', teamMemberId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;


    return { success: true, leads: data || [] };
  } catch (error) {
    return { success: false, error: error.message, leads: [] };
  }
};

// ============================================================================
// HELPER FUNCTION FOR BROWSER GEOLOCATION
// ============================================================================

/**
 * Get user's current location from browser
 * @returns {Promise} Location coordinates
 */
export const getBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

const quoteService = {
  createQuote,
  updateQuoteLocation,
  saveMoveDetails,
  savePropertySize,
  savePianoMove,
  saveTruckContainerMove,
  saveVehicleMove,
  saveProjectDetails,
  sendOTP,
  verifyOTP,
  saveUserAndLinkQuote,
  saveUserLiveLocation,
  getUserLiveLocation,
  getMoversByState,
  saveSelectedMovers,
  createLeadsFromQuote,
  submitQuote,
  deleteQuoteData,
  getCompleteQuote,
  getUserQuotes,
  getMoverLeads,
  getBrowserLocation
};

export default quoteService;
